import "react-popper-tooltip/dist/styles.css"
import "./Tooltip.scss"
import iconCloseWhite from "assets/img/icon-close-white.svg"

import React, { FunctionComponent, useState, ReactNode, useMemo, CSSProperties } from "react"
import { createPortal } from "react-dom"
import { usePopperTooltip } from "react-popper-tooltip"
import { detectOverflow, Placement, ModifierPhases } from "@popperjs/core"
import cx from "classnames"

import { getZIndex } from "utils/modal"

export type TooltipTrigger = "click" | "hover"
type TooltipStyle = "dark" | "light"
export type TooltipPlacement = Placement

export interface ITooltipProps {
    placement?: TooltipPlacement
    minWidth?: CSSProperties["minWidth"]
    maxWidth?: CSSProperties["maxWidth"]
    minHeight?: CSSProperties["minHeight"]
    maxHeight?: CSSProperties["maxHeight"]
    trigger?: TooltipTrigger
    isOpen?: boolean
    content?: ((close: () => void) => ReactNode) | ReactNode
    children?: ReactNode
    disabled?: boolean
    arrow?: boolean
    theme?: TooltipStyle
    contentPadding?: CSSProperties["padding"]
    whiteSpace?: CSSProperties['whiteSpace']
    forceKeepOpen?: boolean
    forceHideCloseButton?: boolean
    delayShow?: number
    delayHide?: number
    disableHide?: boolean
    onOpen?(): void
    onClose?(): void
}

const Tooltip: FunctionComponent<React.PropsWithChildren<ITooltipProps>> = ({
    trigger: triggerProp = "click",
    isOpen,
    minWidth,
    maxWidth = "515px",
    minHeight,
    maxHeight,
    placement = "top",
    content,
    children: triggerEl,
    disabled,
    arrow = true,
    theme = "dark",
    contentPadding,
    whiteSpace,
    forceKeepOpen,
    forceHideCloseButton,
    delayShow,
    delayHide,
    disableHide,
    onOpen,
    onClose,
}) => {
    const [isOverflowed, setIsOverflowed] = useState(false)
    const trigger = disabled ? null : triggerProp

    const modifiers = useMemo(() => ([
        {
            name: "offset",
            enabled: !!arrow,
            options: {
                offset: ({ placement: currentPlacement }) => {
                    if (currentPlacement === "top" || currentPlacement === "bottom") {
                        return [0, 20]
                    }
                    if (currentPlacement.startsWith("top") || currentPlacement.startsWith("bottom")) {
                        return [5, 20]
                    }
                    return [10, 20]
                }
            }
        },
        {
            name: "arrow",
            options: {
                padding: 5
            }
        },
        {
            name: "overflowControl",
            enabled: true,
            phase: "main" as ModifierPhases,
            fn({ state, options }) {
                const overflow = detectOverflow(state, options)
                if (overflow.top > 0 || overflow.bottom > 0) {
                    setIsOverflowed(true)
                }
            }
        }
    ]), [arrow])

    const {
        getArrowProps,
        getTooltipProps,
        setTooltipRef,
        setTriggerRef,
        triggerRef,
        visible,
    } = usePopperTooltip({
        visible: forceKeepOpen || isOpen === true ? true : undefined,
        trigger,
        interactive: true,
        delayShow,
        delayHide,
        onVisibleChange: newVisible => {
            if (newVisible) {
                onOpen?.()
            } else {
                onClose?.()
            }
        },
    }, {
        placement,
        modifiers,
    })

    const zIndex = useMemo(() => getZIndex(), [])

    const handleClick = (e: React.MouseEvent) => {
        if (trigger !== "click") return
        e.stopPropagation()
    }

    const renderTooltipBody = () => {
        const isCloseButtonDisplayed = !forceHideCloseButton && trigger === "click"
        const contentStyle: React.CSSProperties = {
            minWidth, maxWidth, minHeight, zIndex,
            maxHeight: maxHeight ?? (isOverflowed ? "60vh" : undefined),
            padding: contentPadding
        }

        if (isCloseButtonDisplayed) {
            contentStyle.paddingRight = 0
        }

        return createPortal(
            <div

                ref={setTooltipRef}
                {...getTooltipProps({
                    className: cx([
                        "tooltip-container",
                        `tooltip-container--theme-${theme}`,
                        {
                            "tooltip-container--with-arrow": arrow,
                            "disable-hide": disableHide
                        }
                    ]),
                    style: contentStyle,
                })}>
                {!!arrow &&
                    <div
                        {...getArrowProps({
                            className: "tooltip-arrow",
                        })}
                    />
                }
                <div className="tooltip-content" style={{ ...(isCloseButtonDisplayed ? { "paddingRight": "60px" } : undefined), whiteSpace }}>
                    {typeof content === "function"
                        ? content(() => triggerRef?.click())
                        : content
                    }
                    {isCloseButtonDisplayed &&
                        <button
                            type="button"
                            className="tooltip-close"
                            aria-label="Close"
                            data-testid="tooltip-close"
                            onClick={() => triggerRef?.click()}>
                            <img src={iconCloseWhite} alt="" />
                        </button>
                    }
                </div>
            </div>,
            document.body
        )
    }

    const renderTooltipTrigger = () => {
        return (
            <div ref={setTriggerRef}>
                {triggerEl}
            </div>
        )
    }

    if (!content) {
        return <>{triggerEl}</>
    }

    return (
        <div
            onClick={handleClick}>
            {renderTooltipTrigger()}
            {visible &&
                renderTooltipBody()
            }
        </div>
    )
}

export default Tooltip
